import { CHANNEL_ID_BASE } from '../../config';
import { stringToNumber, toJSNumber, tryParseBigInt } from '../numbers';

/** OpenIM composite conversation ids (`si_` private / `sg_`|`g_` group) — not Telegram peer ids. */
export function isOpenImConversationId(entityId: string) {
  return entityId.startsWith('si_') || entityId.startsWith('sg_') || entityId.startsWith('g_');
}

export function isUserId(entityId: string) {
  // Telegram: positive numeric user / negative chat-channel. OpenIM conversation ids must NOT
  // count as users (otherwise MiddleHeader/ContactGreeting treat `si_`/`sg_` as private peers).
  if (isOpenImConversationId(entityId)) {
    return false;
  }
  return !entityId.startsWith('-');
}

/**
 * Bare peer/group id for OpenIM send/API when `_tgimTargetId` is missing.
 * Private `si_{a}_{b}` needs `selfUserId` to pick the other participant.
 */
export function resolveOpenImTargetId(chatId: string, selfUserId?: string): string | undefined {
  if (chatId.startsWith('sg_') || chatId.startsWith('g_')) {
    return chatId.slice(chatId.indexOf('_') + 1) || undefined;
  }
  if (!chatId.startsWith('si_') || !selfUserId) {
    return undefined;
  }
  const rest = chatId.slice(3);
  const prefix = `${selfUserId}_`;
  const suffix = `_${selfUserId}`;
  if (rest.startsWith(prefix)) {
    return rest.slice(prefix.length) || undefined;
  }
  if (rest.endsWith(suffix)) {
    return rest.slice(0, -suffix.length) || undefined;
  }
  return undefined;
}

/** OpenIM single-chat `conversationID` = `si_` + sorted(self, peer).joined `_`. */
export function buildOpenImPrivateChatId(selfUserId: string, peerUserId: string): string {
  return `si_${[selfUserId, peerUserId].sort().join('_')}`;
}

export function isChannelId(entityId: string) {
  const n = BigInt(entityId);
  return n < -CHANNEL_ID_BASE;
}

export function toChannelId(mtpId: string) {
  const n = BigInt(mtpId);
  return String(-CHANNEL_ID_BASE - n);
}

// Non-Telegram backends (e.g. OpenIM) use non-numeric peer ids (like `u_phone_<hex>`), which have
// no MTProto user/chat/channel sign-encoding to decode. Fall back to a deterministic hash so callers
// still get a stable "raw id" to bucket on (e.g. for color assignment) instead of a `BigInt` crash.
export function getRawPeerId(id: string) {
  // Skip BigInt parse (and its DEBUG console.error) for known OpenIM id shapes.
  if (isOpenImConversationId(id) || id.startsWith('u_')) {
    return BigInt(Math.abs(stringToNumber(id)));
  }

  const n = tryParseBigInt(id);
  if (n === undefined) {
    return BigInt(Math.abs(stringToNumber(id)));
  }

  if (isUserId(id)) {
    return n;
  }

  if (isChannelId(id)) {
    return -n - CHANNEL_ID_BASE;
  }

  return n * -1n;
}

export function getPeerIdDividend(peerId: string) {
  return toJSNumber(getRawPeerId(peerId));
}
